import * as React from 'react';

import { cn } from 'lib/utils';

const paginationButtonClasses =
  'inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const Pagination = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'nav'>
>(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
));
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentPropsWithoutRef<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-2', className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('list-none', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = React.ComponentPropsWithoutRef<'button'> & {
  isActive?: boolean;
};

const PaginationLink = React.forwardRef<HTMLButtonElement, PaginationLinkProps>(
  ({ className, isActive, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        paginationButtonClasses,
        isActive &&
          'border-blue-700 bg-blue-700 text-white hover:bg-blue-700 hover:text-white',
        className
      )}
      {...props}
    />
  )
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = React.forwardRef<
  HTMLButtonElement,
  PaginationLinkProps
>(({ children, className, ...props }, ref) => (
  <PaginationLink
    ref={ref}
    className={cn('px-4', className)}
    aria-label="Précédent"
    {...props}
  >
    {children ?? '< Précédent'}
  </PaginationLink>
));
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = React.forwardRef<HTMLButtonElement, PaginationLinkProps>(
  ({ children, className, ...props }, ref) => (
    <PaginationLink
      ref={ref}
      className={cn('px-4', className)}
      aria-label="Suivant"
      {...props}
    >
      {children ?? 'Suivant >'}
    </PaginationLink>
  )
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    role="presentation"
    className={cn(
      'inline-flex min-h-[36px] min-w-[36px] items-center justify-center text-sm text-gray-500',
      className
    )}
    {...props}
  >
    ...
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};

export const getSimplePaginationItems = (page: number, totalPages: number) => {
  const currentPage = Number.isFinite(page) ? Math.max(Math.floor(page), 1) : 1;
  const maxPages = Number.isFinite(totalPages)
    ? Math.max(Math.floor(totalPages), 0)
    : 0;

  const items = new Set<number>();
  items.add(currentPage);

  if (currentPage > 1) {
    items.add(currentPage - 1);
    if (currentPage > 2) {
      items.add(1);
    }
  }

  if (maxPages > 0 && currentPage < maxPages) {
    items.add(currentPage + 1);
    if (currentPage < maxPages - 1) {
      items.add(maxPages);
    }
  }

  return Array.from(items).sort((a, b) => a - b);
};
